import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Tabs, Tab, Card, Spinner } from 'react-bootstrap';
import { authAPI } from '../../services/apiService';
import { getTranslatedError } from '../../utils/errorTranslator';
import { UserOutlined, LockOutlined, PhoneOutlined, MailOutlined, UserAddOutlined, LoginOutlined, CheckCircleOutlined } from '@ant-design/icons';
import './Login.css';

const Login = ({ onLogin, confirmParams, onConfirmParamsConsumed }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState('login'); // 'login' | 'phone'

  // Регистрация
  const [registerData, setRegisterData] = useState({
    login: '',
    password: '',
    phone: '',
    email: '',
  });

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationPhone, setVerificationPhone] = useState('');
  const [verificationSmsSent, setVerificationSmsSent] = useState(false);

  // Предзаполнение формы при переходе с /confirm (лендинг)
  useEffect(() => {
    if (confirmParams && (confirmParams.login || confirmParams.phone || confirmParams.email)) {
      setActiveTab('register');
      setRegisterData((prev) => ({
        ...prev,
        login: confirmParams.login || prev.login,
        phone: confirmParams.phone ? formatPhone(confirmParams.phone) : prev.phone,
        email: confirmParams.email || prev.email,
      }));
      onConfirmParamsConsumed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmParams]);

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    let normalized = digits;
    if (normalized.startsWith('7') || normalized.startsWith('8')) {
      normalized = normalized.slice(1);
    }
    normalized = normalized.slice(0, 10);

    const parts = [];
    if (normalized.length > 0) {
      parts.push(`(${normalized.slice(0, 3)}`);
    }
    if (normalized.length >= 3) {
      parts[0] = `${parts[0]})`;
    }
    if (normalized.length > 3) {
      parts.push(normalized.slice(3, 6));
    }
    if (normalized.length > 6) {
      parts.push(normalized.slice(6, 8));
    }
    if (normalized.length > 8) {
      parts.push(normalized.slice(8, 10));
    }

    if (parts.length === 0) {
      return '+7';
    }
    if (parts.length === 1) {
      return `+7 ${parts[0]}`;
    }
    if (parts.length === 2) {
      return `+7 ${parts[0]} ${parts[1]}`;
    }
    if (parts.length === 3) {
      return `+7 ${parts[0]} ${parts[1]}-${parts[2]}`;
    }
    return `+7 ${parts[0]} ${parts[1]}-${parts[2]}-${parts[3]}`;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.login(login, password);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      onLogin(response.accessToken, response.user);
    } catch (err) {
      setError(getTranslatedError(err, 'Неверный логин или пароль'));
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const email = registerData.email.trim();
    if (!email) {
      setError('Укажите email');
      return;
    }
    if (!isValidEmail(email)) {
      setError('Введите корректный email');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.register({ ...registerData, email });

      if (response?.accessToken && response?.refreshToken) {
        localStorage.setItem('accessToken', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.user));
        onLogin(response.accessToken, response.user);
        return;
      }

      if (response?.requiresVerification) {
        const phone = registerData.phone || response.phone || '';
        const smsSent = response.smsSent !== false;
        setRequiresVerification(true);
        setVerificationPhone(phone);
        setVerificationCode('');
        setVerificationSmsSent(smsSent);
        setLoading(false);
        if (!phone) {
          setError('Укажите номер телефона для подтверждения.');
        } else if (!smsSent) {
          setError(response.smsError?.message || 'Регистрация прошла, но SMS не отправлена. Запросите код повторно.');
        }
        return;
      }

      setRequiresVerification(false);
      setVerificationPhone('');
      setVerificationCode('');
      setVerificationSmsSent(false);
      setError('');
      setActiveTab('login');
      setLogin(registerData.login);
      setLoading(false);
      alert('Регистрация успешна. Войдите в систему.');
    } catch (err) {
      setError(getTranslatedError(err, 'Ошибка при регистрации'));
      setLoading(false);
    }
  };

  const handleVerifyPhone = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.verifyPhone(verificationPhone, verificationCode);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      onLogin(response.accessToken, response.user);
    } catch (err) {
      setError(getTranslatedError(err, 'Неверный код подтверждения'));
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await authAPI.resendVerification(verificationPhone);
      setError('');
      setVerificationSmsSent(true);
      alert('Код подтверждения отправлен повторно');
    } catch (err) {
      setError(getTranslatedError(err, 'Ошибка при отправке кода'));
    }
  };

  if (requiresVerification) {
    return (
      <Card className="login-card">
        <Card.Body className="p-3">
          <div className="text-center mb-3">
            <div className="verification-icon">
              <PhoneOutlined />
            </div>
            <h4 className="mb-1" style={{ fontSize: '18px', fontWeight: '600' }}>Подтверждение телефона</h4>
            <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
              {verificationSmsSent ? (
                <>На номер <strong>{verificationPhone}</strong> отправлен код подтверждения</>
              ) : (
                <>SMS на номер <strong>{verificationPhone}</strong> пока не отправлена — запросите код повторно</>
              )}
            </p>
          </div>
          
          {error && (
            <Alert variant="danger" className="mb-2" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Form onSubmit={handleVerifyPhone}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                <CheckCircleOutlined className="me-2" />
                Код подтверждения
              </Form.Label>
              <Form.Control
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Введите 6-значный код"
                required
                maxLength={6}
                className="verification-input"
                style={{
                  textAlign: 'center',
                  fontSize: '20px',
                  letterSpacing: '6px',
                  fontWeight: '600',
                  padding: '10px 14px',
                }}
              />
            </Form.Group>
            
            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit"
                disabled={loading}
                className="login-button"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Проверка...
                  </>
                ) : (
                  <>
                    <CheckCircleOutlined className="me-2" />
                    Подтвердить
                  </>
                )}
              </Button>
              <Button 
                variant="outline-secondary" 
                type="button"
                onClick={handleResendCode}
                className="login-button"
                style={{ fontSize: '14px' }}
              >
                <PhoneOutlined className="me-2" />
                Отправить код повторно
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="login-card">
      <Card.Body className="p-3">
        <div className="text-center mb-3">
          <div className="login-icon">
            <UserOutlined />
          </div>
          <h3 className="mb-1" style={{ fontSize: '20px', fontWeight: '600' }}>Добро пожаловать</h3>
          <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Войдите в систему или создайте новый аккаунт</p>
        </div>

        {error && (
          <Alert variant="danger" className="mb-2" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Tabs 
          activeKey={activeTab} 
          onSelect={(k) => setActiveTab(k)} 
          className="mb-3 custom-auth-tabs"
        >
          <Tab 
            eventKey="login" 
            title={
              <span className="d-flex align-items-center justify-content-center gap-2">
                <LoginOutlined />
                Вход
              </span>
            }
          >
            <Form onSubmit={handleLoginSubmit}>
              <Form.Group className="mb-2">
                <div className="d-flex gap-3 mb-2">
                  <Form.Check
                    type="radio"
                    id="login-mode-login"
                    label="По логину"
                    name="login-mode"
                    checked={loginMode === 'login'}
                    onChange={() => {
                      setLoginMode('login');
                      setLogin('');
                    }}
                  />
                  <Form.Check
                    type="radio"
                    id="login-mode-phone"
                    label="По телефону"
                    name="login-mode"
                    checked={loginMode === 'phone'}
                    onChange={() => {
                      setLoginMode('phone');
                      setLogin('');
                    }}
                  />
                </div>
                <Form.Label className="fw-bold">
                  {loginMode === 'phone' ? (
                    <>
                      <PhoneOutlined className="me-2" />
                      Телефон
                    </>
                  ) : (
                    <>
                      <UserOutlined className="me-2" />
                      Логин
                    </>
                  )}
                </Form.Label>
                <Form.Control
                  type={loginMode === 'phone' ? 'tel' : 'text'}
                  value={login}
                  onChange={(e) =>
                    setLogin(
                      loginMode === 'phone'
                        ? formatPhone(e.target.value)
                        : e.target.value
                    )
                  }
                  placeholder={
                    loginMode === 'phone'
                      ? '+7 (___) ___-__-__'
                      : 'Введите ваш логин'
                  }
                  required
                  className="form-input"
                  autoComplete={loginMode === 'phone' ? 'tel' : 'username'}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  <LockOutlined className="me-2" />
                  Пароль
                </Form.Label>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Введите пароль"
                  required
                  className="form-input"
                />
              </Form.Group>
              
              <Button 
                variant="primary" 
                type="submit"
                disabled={loading}
                className="w-100 login-button"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Вход...
                  </>
                ) : (
                  <>
                    <LoginOutlined className="me-2" />
                    Войти
                  </>
                )}
              </Button>
            </Form>
          </Tab>

          <Tab 
            eventKey="register" 
            title={
              <span className="d-flex align-items-center justify-content-center gap-2">
                <UserAddOutlined />
                Регистрация
              </span>
            }
          >
            <Form onSubmit={handleRegisterSubmit}>
              <Form.Group className="mb-2">
                <Form.Label className="fw-bold">
                  <UserOutlined className="me-2" />
                  Логин <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={registerData.login}
                  onChange={(e) => setRegisterData({ ...registerData, login: e.target.value })}
                  placeholder="Уникальный логин"
                  required
                  minLength={3}
                  maxLength={50}
                  className="form-input"
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="fw-bold">
                  <LockOutlined className="me-2" />
                  Пароль <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  placeholder="Минимум 6 символов"
                  required
                  minLength={6}
                  className="form-input"
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="fw-bold">
                  <MailOutlined className="me-2" />
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={registerData.email}
                  onChange={(e) =>
                    setRegisterData({ ...registerData, email: e.target.value.replace(/\s/g, '') })
                  }
                  placeholder="user@example.com"
                  required
                  className="form-input"
                  isInvalid={!!registerData.email && !isValidEmail(registerData.email)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  <PhoneOutlined className="me-2" />
                  Телефон <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  value={registerData.phone}
                  onChange={(e) =>
                    setRegisterData({ ...registerData, phone: formatPhone(e.target.value) })
                  }
                  placeholder="+7 (___) ___-__-__"
                  required
                  className="form-input"
                />
              </Form.Group>
              
              <Button 
                variant="success" 
                type="submit"
                disabled={loading}
                className="w-100 login-button"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Регистрация...
                  </>
                ) : (
                  <>
                    <UserAddOutlined className="me-2" />
                    Зарегистрироваться
                  </>
                )}
              </Button>
            </Form>
          </Tab>
        </Tabs>
      </Card.Body>
    </Card>
  );
};

export default Login;
